import { createError } from "../../../../../lib/error";

class PositionDetailsModalController {
  constructor(
    $scope,
    $uibModalInstance,
    $q,
    $window,
    $http,
    position,
    modalClass,
    DateTimeService,
    APIService,
    imageModal
  ) {
    "ngInject";
    this.$scope = $scope;
    this.$uibModalInstance = $uibModalInstance;
    this.$q = $q;
    this.$window = $window;
    this.$http = $http;
    this._position = position;
    this.modalClass = modalClass;
    this.DateTimeService = DateTimeService;
    this.APIService = APIService;
    this.imageModal = imageModal;

    this._errors = [];
    this._mapUrl = null;
    this._mapObjectUrl = null;

    this._loadMap();
    this._loadRequestImages();

    $scope.$on('$destroy', () => this._revokeMapUrl());
  }

  set isOnline(val) {
    this._isOnline = val;
  }

  get isOnline() {
    if (this.APIService) {
      return this.APIService.isOnline;
    }
    return false;
  }

  get position() {
    return this._position;
  }

  get mapUrl() {
    return this._mapUrl;
  }

  get maconomyBrand() {
    if (!this.position.maconomy_enabled) {
      return false
    }
    return this.position?.event?.maconomy_brand
  }

  get positionStartTimeInEventTimezone() {
    const startTime = this.position && this.position.start_time;
    const eventTimezoneName = this.position && this.position.event && this.position.event.timezoneName;
    const moment = this.DateTimeService && this.DateTimeService.parseMomentWithTimezone(startTime, eventTimezoneName);
    return moment;
  }

  get positionEndTimeInEventTimezone() {
    const endTime = this.position && this.position.end_time;
    const eventTimezoneName = this.position && this.position.event && this.position.event.timezoneName;
    const moment = this.DateTimeService && this.DateTimeService.parseMomentWithTimezone(endTime, eventTimezoneName);
    return moment;
  }

  get hasExpirationDate() {
    return !!(this.position && this.position.event && this.position.event.expiration_date);
  }

  get expirationDateInEventTimezone() {
    const expirationDate = this.position && this.position.event && this.position.event.expiration_date;
    const eventTimezoneName = this.position && this.position.event && this.position.event.timezoneName;
    return this.DateTimeService && this.DateTimeService.parseMomentWithTimezone(expirationDate, eventTimezoneName);
  }

  get daysUntilExpiration() {
    const expirationDate = this.position && this.position.event && this.position.event.expiration_date;
    const eventTimezoneName = this.position && this.position.event && this.position.event.timezoneName;
    return this.DateTimeService && this.DateTimeService.daysUntil(expirationDate, eventTimezoneName);
  }

  get requestImages() {
    return (this.position && this.position.request_images) || [];
  }

  get hasRequestImages() {
    return this.requestImages.length > 0;
  }

  // Open a supporting image in a modal (consistent with the Files modal),
  // rather than navigating away to a new browser tab. No-op until the signed
  // URL has resolved (offline, or _loadRequestImages() still in flight).
  viewImage(img) {
    if (!img || !img.url) {
      return;
    }
    this.imageModal({ image: img });
  }

  get canViewEventPDF() {
    return !!this.eventPDFURL;
  }

  get eventPDFURL() {
    return this.position && this.position.event && this.position.event.pdf_url;
  }

  setAvailable() {
    this.$uibModalInstance.close(true);
  }
  setUnavailable() {
    this.$uibModalInstance.close(false);
  }
  cancel() {
    this.$uibModalInstance.dismiss('cancel');
  }
  viewEventPDF() {
    if (!this.eventPDFURL) {
      throw createError({message: "Missing event PDF URL"});
    }

    const eventId = this.position && this.position.event && this.position.event.id || "unknown";

    // Open URL in new window
    return this._openPost(this.eventPDFURL, {
      token: this.APIService.getAuthToken(),
      filename: `event-${eventId}.pdf`,
    });
  }

  // Fetch the static map via the authenticated /api/v1/static_map proxy.
  // <img src> can't carry the staff-app bearer token, so we fetch the image
  // as a blob through $http (Authorization header is set on $http.defaults),
  // then hand the modal an object URL to render.
  _loadMap() {
    const pos = this.position;
    const hasAddress = pos && pos.street_address1 && pos.city && pos.state;
    if (!this.isOnline || pos?.allow_no_location || !hasAddress) {
      return;
    }

    const center = `${pos.street_address1} ${pos.city} ${pos.state} ${pos.zip || ''}`.trim();
    const url = this.APIService.convertPathToURL('/static_map', {
      center,
      zoom: 14,
      size: '320x275',
      maptype: 'roadmap',
      markers: center,
    });

    this.$http.get(url, { responseType: 'blob' })
      .then(resp => {
        this._revokeMapUrl();
        this._mapObjectUrl = this.$window.URL.createObjectURL(resp.data);
        this._mapUrl = this._mapObjectUrl;
      })
      .catch(err => {
        // Swallow — if the map fails, the modal still shows the address as a
        // clickable Google-Maps link. No need to surface an error to staff.
        console.warn('Static map load failed', err);
      });
  }

  // Resolve each supporting-image document to a viewable (signed) URL via the
  // same /document_href endpoint the Files modal uses. Online only; the signed
  // URL is used directly as the <img src>, so no auth header is required and
  // there's nothing to revoke on teardown (unlike the map blob).
  _loadRequestImages() {
    if (!this.isOnline) {
      return;
    }
    this.requestImages.forEach(img => {
      if (!img || !img.id) {
        return;
      }
      this.APIService.get('/document_href/' + img.id, { force: true })
        .then(resp => {
          img.url = resp && resp.data && resp.data.url;
        })
        .catch(err => {
          // Non-fatal: the modal falls back to showing the file name offline.
          console.warn('Request image load failed', err);
        });
    });
  }

  _revokeMapUrl() {
    if (this._mapObjectUrl) {
      this.$window.URL.revokeObjectURL(this._mapObjectUrl);
      this._mapObjectUrl = null;
    }
    this._mapUrl = null;
  }

  // Open a new window with a POST request with specified params
  _openPost(url, params = {}) {
    // See: https://stackoverflow.com/a/5691308
    const document = this.$window.document;

    const form = document.createElement("form");
    form.target = "_blank";
    form.method = "POST";
    form.action = url;

    Object.keys(params).forEach(paramKey => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = paramKey;
      input.value = params[paramKey];
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  }
}

export default PositionDetailsModalController;
